'use client';

import { createRoot, type Root } from 'react-dom/client';

import { NavbarWidget } from '@/features/navbar';
import type { NavItem } from '@/features/navbar';
import { createWidgetMount } from '@/shared/utils/widget-mount';
import {
  DEFAULT_NAVBAR_WIDGET_PROPS,
  OBSERVED_NAVBAR_WIDGET_ATTRIBUTES,
  readNavbarWidgetProps,
} from './navbar-element-props';
import type { NavbarEventName } from './types';

function dispatchNavbarEvent(element: HTMLElement, name: NavbarEventName, detail?: unknown) {
  element.dispatchEvent(
    new CustomEvent(name, {
      detail,
      bubbles: true,
      composed: true,
    }),
  );
}

function parseNavItems(json?: string): NavItem[] | undefined {
  if (!json) return undefined;
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function dispatchSearchEvent(element: HTMLElement, query: string, searchBaseUrl?: string) {
  dispatchNavbarEvent(element, 'adipa-navbar-search', { query });

  if (searchBaseUrl) {
    window.location.href = `${searchBaseUrl}${encodeURIComponent(query)}`;
  }
}

export class NavbarWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_NAVBAR_WIDGET_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;

  get country(): string {
    return this.getAttribute('platform-country') ?? DEFAULT_NAVBAR_WIDGET_PROPS.country;
  }

  set country(value: string) {
    this.setAttribute('platform-country', value);
  }

  get authState(): string {
    return this.getAttribute('auth-state') ?? DEFAULT_NAVBAR_WIDGET_PROPS.authState;
  }

  set authState(value: string) {
    this.setAttribute('auth-state', value);
  }

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.scheduleRender();
  }

  disconnectedCallback() {
    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;
      if (this.isConnected || this.root !== root) return;
      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  private scheduleRender() {
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private renderWidget() {
    if (!this.root) return;

    const props = readNavbarWidgetProps(this);
    // Sin atributo `nav-items` el navbar embebido NO debe renderizar la barra
    // de navegación: pasar `[]` evita que Header caiga a GLOBAL_NAV_ITEMS
    // (ítems demo con href '#'). El default solo aplica con `navItems` undefined.
    const navItems = parseNavItems(props.navItemsJson) ?? [];
    const user =
      props.authState === 'authenticated' && props.userName
        ? { name: props.userName, email: props.userEmail, avatarUrl: props.userAvatarUrl }
        : undefined;

    dispatchNavbarEvent(this, 'adipa-navbar-ready', {
      country: props.country,
      authState: props.authState,
    });

    this.root.render(
      <NavbarWidget
        country={props.country}
        authState={props.authState}
        user={user}
        logoUrl={props.logoUrl}
        homeUrl={props.homeUrl}
        loginUrl={props.loginUrl}
        registerUrl={props.registerUrl}
        accountUrl={props.accountUrl}
        classroomUrl={props.classroomUrl}
        classroomBadge={props.classroomBadge}
        labUrl={props.labUrl}
        labBadge={props.labBadge}
        profileUrl={props.profileUrl}
        logoutUrl={props.logoutUrl}
        cartItemCount={props.cartItemCount}
        cartLoading={props.cartLoading}
        searchBaseUrl={props.searchBaseUrl}
        searchPlaceholder={props.searchPlaceholder}
        searchAutoSuggest={props.searchAutoSuggest}
        searchSuggestUrl={props.searchSuggestUrl}
        searchDebounceMs={props.searchDebounceMs}
        searchMinLength={props.searchMinLength}
        searchMaxResults={props.searchMaxResults}
        sticky={props.sticky}
        hideUserActions={props.hideUserActions}
        hideLogin={props.hideLogin}
        navItems={navItems}
        onSearch={(query) => dispatchSearchEvent(this, query, props.searchBaseUrl)}
        onLoginClick={() => dispatchNavbarEvent(this, 'adipa-navbar-login-click')}
        onLogoutClick={() => dispatchNavbarEvent(this, 'adipa-navbar-logout-click')}
        onCartClick={() => dispatchNavbarEvent(this, 'adipa-navbar-cart-click')}
        onNavClick={(item) =>
          dispatchNavbarEvent(this, 'adipa-navbar-nav-click', {
            href: item.href,
            label: item.label,
            url: item.url,
          })
        }
      />,
    );
  }
}
